import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { api } from "../api";
import { ArrowLeft, Phone, MessageCircle, Mail, CheckCircle2 } from "lucide-react";
import { useSettings } from "../settings";
import { waLink } from "../components/WhatsAppFloat";

const ProductDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);
  const { settings } = useSettings();

  useEffect(() => {
    setActive(0);
    api.productBySlug(slug).then(setP).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <main className="pt-[140px] pb-24 text-center bg-white">
        <h1 className="text-2xl font-black text-[#1a1a1a]">Ürün bulunamadı</h1>
        <Link to="/urunlerimiz" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#4E0D1C] hover:text-[#C9A24B]">
          <ArrowLeft className="w-4 h-4" /> Tüm ürünler
        </Link>
      </main>
    );
  }

  if (!p) return <main className="pt-32 text-center text-neutral-500">Yükleniyor…</main>;

  const gallery = [p.cover_image, ...(p.images || [])].filter(Boolean);
  const mainImg = gallery[active] || p.cover_image;
  const wa = waLink(settings.whatsapp, `${p.title} ürünü hakkında bilgi almak istiyorum.`);

  return (
    <main className="bg-white">
      <SEO title={p.title} description={p.subtitle || p.description} path={`/urun/${p.slug}`} image={p.cover_image} />

      <section className="pt-[110px] md:pt-[140px] pb-6 md:pb-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Link to="/urunlerimiz" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C9A24B] hover:text-[#4E0D1C]">
            <ArrowLeft className="w-3.5 h-3.5" /> Ürünlerimiz
          </Link>

          <div className="mt-6 grid lg:grid-cols-2 gap-8 md:gap-12">
            {/* Gallery */}
            <div>
              <div className="rounded-2xl overflow-hidden bg-neutral-100 aspect-[4/3]">
                {mainImg ? (
                  <img src={mainImg} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-neutral-400 text-sm">Görsel yok</div>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={`rounded-lg overflow-hidden aspect-square bg-neutral-100 border-2 transition-colors ${
                        active === i ? "border-[#4E0D1C]" : "border-transparent hover:border-neutral-300"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#C9A24B] font-semibold">{p.category}</div>
              <h1 className="mt-2 text-3xl md:text-5xl font-black text-[#1a1a1a] leading-[1.1]">{p.title}</h1>
              {p.subtitle && <p className="mt-4 text-lg text-neutral-600">{p.subtitle}</p>}

              {p.description && (
                <div className="mt-6 text-neutral-700 text-[16px] leading-relaxed whitespace-pre-line">
                  {p.description}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white text-sm font-semibold transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp’tan Sor
                  </a>
                )}
                <a
                  href={`tel:${(settings.phone || "").replace(/\s+/g, "")}`}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#4E0D1C] hover:bg-[#C9A24B] text-white text-sm font-semibold transition-colors"
                >
                  <Phone className="w-4 h-4" /> Bize Ulaşın
                </a>
                <a
                  href={`mailto:${settings.email}?subject=${encodeURIComponent(p.title + " hakkında")}`}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full border border-neutral-200 text-[#1a1a1a] hover:bg-neutral-50 text-sm font-semibold transition-colors"
                >
                  <Mail className="w-4 h-4" /> Teklif İste
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      {p.specs?.length > 0 && (
        <section className="py-12 md:py-16 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a]">Teknik Şartname</h2>
            <div className="mt-6 grid md:grid-cols-2 gap-3">
              {p.specs.map((s, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-neutral-100">
                  <CheckCircle2 className="w-5 h-5 text-[#C9A24B] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">{s.label}</div>
                    <div className="mt-1 text-[#1a1a1a] font-semibold">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
